import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useAnimationControls } from "framer-motion";

interface Props {
  value: string;
  placeholder: string;
  disabled?: boolean;
  error?: string;
  success?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const ReferralInput = ({
  value,
  placeholder,
  onChange,
  onKeyDown,
  disabled,
  error,
  success,
}: Props) => {
  const [isFocused, setIsFocused] = useState(false);
  const [scanKey, setScanKey] = useState(0);
  const [showScan, setShowScan] = useState(false);
  const [typingKey, setTypingKey] = useState(0);
  const [showTypingFlash, setShowTypingFlash] = useState(false);

  const shakeControls = useAnimationControls();
  const prevError = useRef(error);
  const isFocusedRef = useRef(false);
  const isFirstRender = useRef(true);

  const hasError = !!error;
  const isEmptyInput = !value;

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleFocus = () => {
    if (disabled) return;
    isFocusedRef.current = true;
    setIsFocused(true);
    setScanKey((k) => k + 1);
    setShowScan(true);
    setTimeout(() => setShowScan(false), 650);
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    setIsFocused(false);
  };

  // ── Shake on new error ───────────────────────────────────────────────────
  useEffect(() => {
    if (!prevError.current && error) {
      shakeControls.start({
        x: [0, -9, 9, -6, 6, -3, 3, 0],
        transition: { duration: 0.45, ease: "easeOut" },
      });
    }
    prevError.current = error;
  }, [error, shakeControls]);

  // ── Per-keystroke trailing flash ─────────────────────────────────────────
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isFocusedRef.current) return;
    setTypingKey((k) => k + 1);
    setShowTypingFlash(true);
    const t = setTimeout(() => setShowTypingFlash(false), 220);
    return () => clearTimeout(t);
  }, [value]);

  // ── Derived visual states ─────────────────────────────────────────────────
  // Accent bar: array = Framer Motion keyframes (breathing pulse)
  const accentBarColor: string | string[] = hasError
    ? "#ef4444"
    : success
      ? "#22c55e"
      : isFocused
        ? ["#48A7FF", "#85caff", "#48A7FF"]
        : !isEmptyInput
          ? "#48A7FF"
          : "#ffffff";

  const boxShadow = hasError
    ? "0 0 0 1px rgba(239,68,68,0.75), 0 0 16px rgba(239,68,68,0.28)"
    : success
      ? "0 0 0 1px rgba(34,197,94,0.75), 0 0 16px rgba(34,197,94,0.28)"
      : showScan
        ? [
            "0 0 0 1px #48A7FF, 0 0 6px rgba(72,167,255,0.15)",
            "0 0 0 1px #48A7FF, 0 0 24px rgba(72,167,255,0.55)",
            "0 0 0 1px #48A7FF, 0 0 10px rgba(72,167,255,0.22)",
          ]
        : isFocused
          ? "0 0 0 1px #48A7FF, 0 0 10px rgba(72,167,255,0.22)"
          : "0 0 0 1px transparent";

  const inputBg = hasError
    ? "from-[#3a1a1a] via-[#3a1a1a]/40 to-transparent"
    : success
      ? "from-[#1a3a1e] via-[#1a3a1e]/40 to-transparent"
      : "from-[#233a4d] via-[#233a4d]/40 to-transparent";

  return (
    <div className="flex flex-col gap-1">
      <motion.div animate={shakeControls}>
        <motion.div
          animate={{ boxShadow }}
          transition={
            showScan && !hasError && !success
              ? { duration: 0.6, times: [0, 0.3, 1], ease: "easeOut" }
              : { duration: 0.3 }
          }
          className="relative rounded-xl overflow-hidden bg-gradient-to-r from-[#5c6b78] via-[#233a4d]/40 to-transparent p-[2px]"
        >
          {/* ── Scan line — fires once on focus ──────────────────────────── */}
          <AnimatePresence>
            {showScan && (
              <motion.span
                key={`scan-${scanKey}`}
                className="absolute inset-y-0 w-[55%] pointer-events-none z-10"
                style={{
                  background:
                    "linear-gradient(to right, transparent, rgba(72,167,255,0.22), transparent)",
                }}
                initial={{ left: "-55%" }}
                animate={{ left: "155%" }}
                transition={{ duration: 0.55, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>

          {/* ── Per-keystroke trailing flash ──────────────────────────────── */}
          <AnimatePresence>
            {showTypingFlash && (
              <motion.span
                key={`type-${typingKey}`}
                className="absolute inset-y-0 right-0 w-[45%] pointer-events-none z-10"
                style={{
                  background:
                    "linear-gradient(to left, rgba(72,167,255,0.2), transparent)",
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: 0.22, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>

          {/* ── Input ─────────────────────────────────────────────────────── */}
          <div
            className={`relative rounded-xl overflow-hidden bg-gradient-to-r ${inputBg}`}
          >
            <input
              type="text"
              disabled={disabled}
              value={value}
              onChange={onChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              aria-label="Referral code input"
              aria-invalid={hasError}
              className={[
                "border-0 outline-none bg-transparent min-h-[77px] p-4 pl-9 w-full font-bold text-[35px] placeholder:text-white/20",
                hasError ? "text-red-300" : "",
                success ? "text-green-300" : "text-white",
                disabled ? "opacity-50 cursor-not-allowed" : "cursor-text",
              ].join(" ")}
            />

            {/* ── Left accent bar ───────────────────────────────────────── */}
            <motion.div
              className="absolute left-4 top-5 bottom-5 w-[5px] rounded-2xl shadow-xl"
              animate={{ backgroundColor: accentBarColor as string }}
              transition={
                isFocused && !hasError && !success
                  ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
                  : { duration: 0.3 }
              }
            />
          </div>
        </motion.div>
      </motion.div>

      {/* ── Error message ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -5, x: -3 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: "backOut" }}
            className="text-xs text-red-400 pl-2 flex items-center gap-1.5"
            role="alert"
          >
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"
              animate={{ opacity: [1, 0.3, 1], scale: [1, 0.7, 1] }}
              transition={{
                duration: 1.0,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReferralInput;
